import { useMutation, useQueryClient } from '@tanstack/react-query';
import { aiApplicationsClient } from '@terra/shared-rest-clients-ai-applications';
import type { UpdateAiApplicationDto } from '@terra/shared-contracts-ai-applications';
import {
  createApiHeaders,
  withOrchestratorClient,
} from '../../clients/tester-client/api-core';
import { useProjectStore } from '../../store/projectStore';
import { TARGETS_QUERY_KEY } from '../useTargets';
import { AI_APPLICATION_KEY } from './useAiApplication';
import { AI_APPLICATION_URL_EDIT_RESTRICTION_KEY } from './useAiApplicationUrlEditRestriction';

const withHeaders = async () =>
  withOrchestratorClient(await createApiHeaders());

const readErrorMessage = (body: unknown): string | undefined => {
  if (typeof body !== 'object' || body == null) {
    return undefined;
  }

  const { message } = body as Record<string, unknown>;

  return typeof message === 'string' && message.length > 0
    ? message
    : undefined;
};

export function useUpdateAiApplication({
  aiApplicationId,
}: {
  aiApplicationId: string;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateAiApplicationDto) => {
      const response = await aiApplicationsClient.update({
        params: { aiApplicationId },
        body,
        ...(await withHeaders()),
      });

      if (response.status !== 200) {
        throw new Error(
          readErrorMessage(response.body) ?? 'Failed to update AI application',
        );
      }

      return response.body;
    },
    onSuccess: (updatedApplication, variables) => {
      queryClient.invalidateQueries({
        queryKey: [AI_APPLICATION_KEY, aiApplicationId],
      });
      queryClient.invalidateQueries({
        queryKey: [AI_APPLICATION_URL_EDIT_RESTRICTION_KEY, aiApplicationId],
      });
      queryClient.invalidateQueries({
        queryKey: [TARGETS_QUERY_KEY],
      });

      if (variables.url !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ['application-credentials', aiApplicationId],
        });
      }

      const { selectedTarget, targetId, updateSelectedTarget } =
        useProjectStore.getState();

      if (
        selectedTarget &&
        targetId === aiApplicationId &&
        updatedApplication.url
      ) {
        updateSelectedTarget({
          ...selectedTarget,
          url: updatedApplication.url,
        });
      }
    },
  });
}

export function useTestMcpConnection({
  aiApplicationId,
}: {
  aiApplicationId: string;
}) {
  return useMutation({
    mutationFn: async ({ identityId }: { identityId?: string } = {}) => {
      const response = await aiApplicationsClient.testMcpConnection({
        params: { aiApplicationId },
        body: identityId ? { identityId } : {},
        ...(await withHeaders()),
      });

      if (response.status !== 200) {
        throw new Error('Failed to test MCP connection');
      }

      return response.body;
    },
  });
}
